import {Component, Inject, OnDestroy, OnInit, Optional} from '@angular/core';
import {
  DeliveryTypeAttachment,
  DeliveryTypeChannel,
  DeliveryTypeWithFee,
  DeliveryTypeWithFeePost,
  DeliveryTypeWithFeePut,
  FeePeriodPrice,
  FeePeriodPricePost,
  FeeType,
  Period,
  TranslationValue
} from '../../../generated/sales-terminal-client';
import {LanguageService} from '../../../services/language/language.service';
import {AuthService} from '../../../services/auth/auth.service';
import {cloneDeep, indexOf} from 'lodash-es';
import {Observable, Subscription} from 'rxjs';
import {MAT_DIALOG_DATA, MatDialog, MatDialogRef} from '@angular/material/dialog';
import {PendingChangesDialogComponent} from '../../pending-changes-dialog/pending-changes-dialog.component';
import {CONTINUE_WITHOUT_SAVING} from '../../../modules/sales-terminal/sales-terminal.constants';
import {TranslateService} from '@ngx-translate/core';
import {finalize} from 'rxjs/operators';
import {SnackService} from '../../../services/snack/snack.service';
import {SortedFee} from '../service-fees/service-fees.component';
import {UtilService} from '../../../services/util/util.service';
import {NumericInputType} from '../../forms/numeric-input/numeric-input.component';
import {DatePickerConfig} from '../../datepicker/datepicker.component';
import moment from 'moment-timezone';
import {ValidationService} from '../../../services/validation/validation.service';
import {SortedDeliveryTypeWithFee} from "../delivery-types/delivery-types.component";
import {EditorType, LanguageField} from "../../lang-tabs/lang-tabs.component";
import {FeeService} from "../../../services/fee/fee.service";
import {DeliveryTypeService} from '../../../services/delivery-type/delivery-type.service';
import {MatSlideToggleChange} from '@angular/material/slide-toggle';

export const DELIVERY_TYPE_CLASSIFIER_MAX_LENGTH: number = 50;
export const DELIVERY_TYPE_NAME_MAX_LENGTH: number = 100;
export const DELIVERY_TYPE_DESCRIPTION_MAX_LENGTH: number = 500;
export const ATTACHMENTS: DeliveryTypeAttachment[] = ['TICKET_BARCODE', 'TICKET_QR', 'TICKET_PDF', 'TICKET_WALLET', 'TERMS_CONDITIONS_PDF', 'ORDER_CONFIRMATION_PDF'];

export interface ActivePeriodPrice extends FeePeriodPrice {
  uniqueId?: string;
}

@Component({
  selector: 'app-delivery-type-dialog',
  templateUrl: './delivery-type-dialog.component.html',
  styleUrls: ['./delivery-type-dialog.component.scss']
})
export class DeliveryTypeDialogComponent implements OnInit, OnDestroy {
  public isLoading: boolean;
  public deliveryType: SortedDeliveryTypeWithFee;
  public activeTab: number = 0;
  public activePeriodPrice: ActivePeriodPrice;
  public sortedLanguages: string[] = [];
  public selectedLanguages: string[] = [];
  public translationFields: LanguageField[] = [];
  public isWebShopEnabled: boolean = false;
  public isBoEnabled: boolean = false;
  public isPeriodCreate: boolean;
  public showEndDatepicker: boolean;
  public isPeriodStartValid: boolean = true;
  public isPeriodEndValid: boolean = true;
  public datePickerConfig: DatePickerConfig = {
    errorMessage: 'general.api_fees_time_invalid',
    timepicker: true, autoBindDates: true, allowDateClear: true, noHint: true, noPresets: true,
    minDate: moment.utc().toISOString()
  };
  private _subs: Subscription[] = [];
  private _originalNames: string;
  private _originalDescriptions: string;
  private _origRefundableValue: boolean;
  private _origIsBoEnabled: boolean;
  private _origIsWebShopEnabled: boolean;
  private _origAttachments: DeliveryTypeAttachment[];
  private _origEmailSubjectKey: string;
  private _origEmailBodyKey: string;
  public minEndDate: string;
  public legalVatPercentage: number;
  public areTranslationsValid: boolean;
  public periodPricesForPost: Record<string, FeePeriodPricePost> = {};

  constructor(public utilService: UtilService,
              public dialogRef: MatDialogRef<DeliveryTypeDialogComponent>,
              @Optional() @Inject(MAT_DIALOG_DATA) public data: DeliveryTypeWithFee,
              public authService: AuthService,
              private dialog: MatDialog,
              private languageService: LanguageService,
              private translateService: TranslateService,
              private deliveryService: DeliveryTypeService,
              private feeService: FeeService,
              private snackService: SnackService) {
    this.deliveryType = data || {
      classifierName: "",
      name: [],
      description: [],
      salesChannels: [],
      attachments: [],
      sortedFee: {
        periodPrices: [],
        futurePeriodPrices: [],
        pastPeriodPrices: [],
        currentAndFuturePeriodPrices: []
      },
      fee: {
        periodPrices: [],
        type: FeeType.Delivery
      }
    };
  }

  public ngOnInit(): void {
    this.isLoading = true;
    this.legalVatPercentage = this.authService.salespoint.legalPerson?.vatPercent;
    this.languageService.getLanguages().pipe(
      finalize(() => this.isLoading = false)
    ).subscribe((results: string[]) => {
      this.sortedLanguages = this.languageService.sortLanguages(results, this.authService.salesPointLanguage);
      this.deliveryType.name = this.languageService.sortWebShopLanguagesForTranslations(this.deliveryType.name, this.authService.salesPointLanguage);
      this.deliveryType.description = this.languageService.sortWebShopLanguagesForTranslations(this.deliveryType.description, this.authService.salesPointLanguage);
      this._originalNames = JSON.stringify(this.prepareTranslations(this.deliveryType?.name));
      this._originalDescriptions = JSON.stringify(this.prepareTranslations(this.deliveryType?.description));
      this.validateTranslations();
    });
    this._origRefundableValue = this.deliveryType?.fee?.isRefundable;
    this.isBoEnabled = this.deliveryType?.salesChannels?.includes(DeliveryTypeChannel.Bo);
    this.isWebShopEnabled = this.deliveryType?.salesChannels?.includes(DeliveryTypeChannel.Webshop);
    this._origIsBoEnabled = this.isBoEnabled;
    this._origIsWebShopEnabled = this.isWebShopEnabled;
    this._origAttachments = this.deliveryType?.attachments;
    this._origEmailSubjectKey = this.deliveryType?.emailSubjectKey;
    this._origEmailBodyKey = this.deliveryType?.emailBodyKey;
    this.dialogRef.disableClose = true;
    this._subs.push(this.dialogRef.backdropClick().subscribe(() => {
      this.confirmClose();
    }));
    this.initializeTranslations();
  }

  public ngOnDestroy(): void {
    this._subs.forEach(s => s.unsubscribe());
    this._subs = [];
  }

  public initializeTranslations() {
    this.translationFields = [
      {
        name: 'name', label: 'general.api_delivery_types_table_name', type: EditorType.Text,
        maxLength: DELIVERY_TYPE_NAME_MAX_LENGTH, required: (language: string) => this.authService.salesPointLanguage === language,
        translations: this.deliveryType.name
      },
      {
        name: 'description', label: 'general.api_delivery_types_table_description', type: EditorType.TextArea,
        maxLength: DELIVERY_TYPE_DESCRIPTION_MAX_LENGTH, required: () => false,
        translations: this.deliveryType.description
      }
    ];
    this.selectedLanguages = [this.authService.salesPointLanguage];

  }

  public saveDeliveryType(): void {
    this.isLoading = true;
    if (!this.deliveryType.id) {
      this.deliveryService.createDeliveryType(this.getDeliveryTypePost).pipe(
        finalize(() => this.isLoading = false)
      ).subscribe((_: DeliveryTypeWithFee) => {
        this.snackService.showSuccess(this.translateService.instant('general.api_delivery_types_create_success'));
        this.dialogRef.close();
      });
    } else {
      this.deliveryService.updateDeliveryType(this.deliveryType.id, this.getDeliveryTypePut).pipe(
        finalize(() => this.isLoading = false)
      ).subscribe((_: DeliveryTypeWithFee) => {
        this.snackService.showSuccess(this.translateService.instant('general.api_delivery_types_save_success'));
        this.dialogRef.close();
      });
    }
  }

  private get getDeliveryTypePost(): DeliveryTypeWithFeePost {
    const deliveryTypePost: any = cloneDeep(this.deliveryType);
    delete deliveryTypePost.id;
    this.deletePeriodPriceHelpers(deliveryTypePost.fee);
    deliveryTypePost.fee.periodPrices = Object.values(this.periodPricesForPost);
    deliveryTypePost.name = this.prepareTranslations(this.deliveryType.name);
    deliveryTypePost.description = this.prepareTranslations(this.deliveryType.description);
    deliveryTypePost.salesChannels = [];
    if (this.isBoEnabled) {
      deliveryTypePost.salesChannels.push(DeliveryTypeChannel.Bo);
    }
    if (this.isWebShopEnabled) {
      deliveryTypePost.salesChannels.push(DeliveryTypeChannel.Webshop);
    }
    return deliveryTypePost as DeliveryTypeWithFeePost;
  }

  private get getDeliveryTypePut(): DeliveryTypeWithFeePut {
    const deliveryTypePut: any = cloneDeep(this.deliveryType);
    delete deliveryTypePut.periodPrices;
    this.deletePeriodPriceHelpers(deliveryTypePut.fee);
    deliveryTypePut.name = this.prepareTranslations(this.deliveryType.name);
    deliveryTypePut.description = this.prepareTranslations(this.deliveryType.description);
    deliveryTypePut.salesChannels = [];
    if (this.isBoEnabled) {
      deliveryTypePut.salesChannels.push(DeliveryTypeChannel.Bo);
    }
    if (this.isWebShopEnabled) {
      deliveryTypePut.salesChannels.push(DeliveryTypeChannel.Webshop);
    }
    return deliveryTypePut as DeliveryTypeWithFeePut;
  }

  private deletePeriodPriceHelpers(fee: SortedFee): SortedFee {
    delete fee.pastPeriodPrices;
    delete fee.currentPeriodPrice;
    delete fee.futurePeriodPrices;
    delete fee.currentAndFuturePeriodPrices;
    return fee;
  }

  private prepareTranslations(translations: TranslationValue[]): TranslationValue[] {
    return translations.filter(translation => translation.value?.length);
  }

  public get isValid(): boolean {
    return !!this.deliveryType?.classifierName?.length && this.deliveryType?.classifierName?.length <= DELIVERY_TYPE_CLASSIFIER_MAX_LENGTH &&
      this.areTranslationsValid && !!this.deliveryType.name.find(name => name.language === this.authService.salesPointLanguage && !!name.value?.length) &&
      (this.isBoEnabled || this.isWebShopEnabled) && this.deliveryType.emailBodyKey?.length > 0 && this.deliveryType.emailSubjectKey?.length > 0 &&
      (this.deliveryType.attachments.includes(DeliveryTypeAttachment.TicketBarcode) || this.deliveryType.attachments.includes(DeliveryTypeAttachment.TicketQr));
  }

  public validateTranslations(): void {
    this.areTranslationsValid = !!this.deliveryType.name?.length &&
      !this.deliveryType.name.some(name => name.value?.length > DELIVERY_TYPE_NAME_MAX_LENGTH) &&
      !this.deliveryType.description.some(description => description.value?.length > DELIVERY_TYPE_DESCRIPTION_MAX_LENGTH);
  }

  public addPeriodPrice(): void {
    this.activePeriodPrice = { id: null, feeAmount: null, vatPercentage: this.legalVatPercentage, period: { start: null, end: null } };
    this.activePeriodPrice.uniqueId = Date.now().toString();
    this.isPeriodCreate = true;
  }

  public editPeriodPrice(feePeriodPrice: FeePeriodPrice, uniqueId?: string): void {
    this.showEndDatepicker = !!feePeriodPrice.period?.end;
    this.activePeriodPrice = {
      id: feePeriodPrice.id,
      uniqueId: uniqueId,
      feeAmount: feePeriodPrice.feeAmount != null ? (feePeriodPrice.feeAmount / 100) : null,
      vatPercentage: feePeriodPrice.vatPercentage,
      period: cloneDeep(feePeriodPrice.period),
    };
  }

  public removePeriodPrice(periodPriceId: number, uniqueId?: string): void {
    if (!periodPriceId && uniqueId) {
      delete this.periodPricesForPost[uniqueId];
    } else {
      this.isLoading = true;
      this.deliveryService.deleteDeliveryTypePeriodPrice(this.deliveryType.id, periodPriceId).pipe(
        finalize(() => this.finalizeRequest())
      ).subscribe((deliveryType: DeliveryTypeWithFee) => this.finishPeriodSave(deliveryType, true));
    }
  }

  public savePeriodPrice(uniqueId?: string): void {
    this.isLoading = true;
    this.activePeriodPrice.feeAmount = this.activePeriodPrice.feeAmount != null ? (this.activePeriodPrice.feeAmount * 100) : null;
    this.activePeriodPrice.vatPercentage = this.activePeriodPrice.vatPercentage != null ? +this.activePeriodPrice.vatPercentage : this.legalVatPercentage;
    const periodPricePost: FeePeriodPricePost = {
      feeAmount: this.activePeriodPrice.feeAmount,
      vatPercentage: this.activePeriodPrice.vatPercentage,
      period: this.activePeriodPrice.period
    };
    if (this.deliveryType.id) {
      if (this.activePeriodPrice.id) {
        this.deliveryService.updateDeliveryTypePeriodPrice(this.deliveryType.id, this.activePeriodPrice.id, this.activePeriodPrice).pipe(
          finalize(() => this.finalizeRequest())
        ).subscribe((deliveryType: DeliveryTypeWithFee) => this.finishPeriodSave(deliveryType));
      } else {
        this.deliveryService.addDeliveryTypePeriodPrice(this.deliveryType.id, periodPricePost).pipe(
          finalize(() => this.finalizeRequest())
        ).subscribe((deliveryType: DeliveryTypeWithFee) => this.finishPeriodSave(deliveryType));
      }
    } else {
      this.periodPricesForPost[uniqueId] = periodPricePost;
      this.finalizeRequest();
    }
  }

  private finalizeRequest(): void {
    this.isLoading = false;
    this.clearActivePeriodPrice();
  }

  private finishPeriodSave(deliveryType: SortedDeliveryTypeWithFee, removal?: boolean): void {
    if (removal) {
      this.snackService.showSuccess(this.translateService.instant('general.api_fees_period_remove_success'));
    } else {
      this.snackService.showSuccess(this.translateService.instant('general.api_fees_period_save_success'));
    }
    deliveryType.sortedFee = this.feeService.sortServiceFeePeriods(deliveryType.fee);
    this.deliveryType = deliveryType;
  }

  public updateAttachment(change: MatSlideToggleChange, attachment: DeliveryTypeAttachment): void {
    if (change.checked && !this.deliveryType.attachments.includes(attachment)) {
      this.deliveryType.attachments.push(attachment);
    } else {
      this.deliveryType.attachments.splice(indexOf(this.deliveryType.attachments, attachment), 1);
    }
  }
  public isAttachementRequired(attachment: DeliveryTypeAttachment): boolean {
    if (attachment === DeliveryTypeAttachment.TicketQr) {
      return !this.deliveryType.attachments.includes(DeliveryTypeAttachment.TicketBarcode) || this.deliveryType.attachments.includes(attachment);
    } else if (attachment === DeliveryTypeAttachment.TicketBarcode) {
      return !this.deliveryType.attachments.includes(DeliveryTypeAttachment.TicketQr) || this.deliveryType.attachments.includes(attachment);
    }
    return false;
  }

  public clearActivePeriodPrice(): void {
    this.activePeriodPrice = null;
    this.showEndDatepicker = false;
    this.isPeriodStartValid = true;
    this.isPeriodEndValid = true;
    this.isPeriodCreate = false;
  }

  public isActivePeriodPrice(id: number, uniqueId?: string): boolean {
    return (!!id && this.activePeriodPrice?.id === id) || (!!uniqueId && this.activePeriodPrice?.uniqueId === uniqueId);
  }

  public isCurrentPeriodPrice(id: number, start: string): boolean {
    const currentUniqueId: string = this.deliveryType.sortedFee?.currentPeriodPrice?.id + this.deliveryType.sortedFee?.currentPeriodPrice?.period?.start;
    const periodUniqueId: string = id + start;
    return currentUniqueId === periodUniqueId;
  }

  public validatePeriodStart(): void {
    const start: moment.Moment = moment(this.activePeriodPrice.period?.start || null);
    const now: moment.Moment = moment(new Date());
    this.isPeriodStartValid = !this.activePeriodPrice.period?.start ||
      (start.isValid() && start.isAfter(now) && !this.isDuplicatePeriodStart(start, this.activePeriodPrice.id));
    if (!this.activePeriodPrice.period?.end && this.isPeriodStartValid) {
      this.isPeriodStartValid = !this.isPeriodOverlapping();
    }
    this.validatePeriodEnd();
  }

  public validatePeriodEnd(): void {
    const now: moment.Moment = moment(new Date());
    const start: moment.Moment = moment(this.activePeriodPrice.period?.start || null);
    const end: moment.Moment = moment(this.activePeriodPrice.period?.end || null);
    this.isPeriodEndValid = !this.activePeriodPrice.period?.end || (end.isValid() && end.isAfter(start) && end.isSameOrAfter(now) && !this.isPeriodOverlapping());
    this.updateMinEndDate();
  }

  private isDuplicatePeriodStart(start: moment.Moment, id: number): boolean {
    return this.deliveryType.fee.periodPrices?.length > 0 && this.deliveryType.fee.periodPrices.filter((periodPrice: FeePeriodPrice) =>
      periodPrice.id !== id && moment(periodPrice.period?.start || null).isSame(start)).length > 0;
  }

  private isPeriodOverlapping(): boolean {
    if (this.periodPricesForPostList?.length) {
      return Object.values(this.periodPricesForPost).some(periodPrice => this.checkOverlap(periodPrice.period));
    } else {
      return this.deliveryType.fee.periodPrices.some(periodPrice => this.activePeriodPrice.id !== periodPrice.id && this.checkOverlap(periodPrice.period));
    }
  }

  private checkOverlap(periodPricePeriod: Period): boolean {
    const activeStart: moment.Moment = moment(this.activePeriodPrice.period?.start || null);
    const activeEnd: moment.Moment = moment(this.activePeriodPrice.period?.end || null);
    const start: moment.Moment = moment(periodPricePeriod?.start || null);
    const end: moment.Moment = moment(periodPricePeriod?.end || null);
    return (activeStart.isAfter(start) && activeStart.isBefore(end)) || (activeEnd.isValid() && activeEnd.isAfter(start) && activeEnd.isBefore(end));
  }

  public setDefaultEndDate(): void {
    this.updateMinEndDate();
    this.activePeriodPrice.period.end = moment(this.minEndDate).set('h', 23).set('m', 59).set('s', 0).toISOString();
    this.showEndDatepicker = true;
  }

  public updateMinEndDate(): void {
    const now: moment.Moment = moment(new Date());
    const start: moment.Moment = moment(this.activePeriodPrice.period?.start || null);
    this.minEndDate = start.isBefore(now) ? now.toISOString() : start.toISOString();
  }

  public get canSavePeriodPrice(): boolean {
    return ValidationService.validatePrice(this.activePeriodPrice.feeAmount) && this.activePeriodPrice.feeAmount != null &&
      !!this.activePeriodPrice.period?.start && this.isPeriodStartValid && this.isPeriodEndValid &&
      ValidationService.validatePercentage(this.activePeriodPrice.vatPercentage, true);
  }

  public get isModelChanged(): boolean {
    return !this.deliveryType?.id ||
      this._originalNames !== JSON.stringify(this.prepareTranslations(this.deliveryType?.name)) ||
      this._originalDescriptions !== JSON.stringify(this.prepareTranslations(this.deliveryType?.description)) ||
      this.deliveryType?.fee?.isRefundable !== this._origRefundableValue || this.isBoEnabled !== this._origIsBoEnabled ||
      this.isWebShopEnabled !== this._origIsWebShopEnabled || JSON.stringify(this.deliveryType?.attachments) !== JSON.stringify(this._origAttachments) ||
      this.deliveryType?.emailSubjectKey !== this._origEmailSubjectKey || this.deliveryType?.emailBodyKey !== this._origEmailBodyKey;
  }

  public confirmClose(): void {
    if (!this.isModelChanged) {
      this.close();
    } else {
      const pendingChanges: PendingChangesDialogComponent = new PendingChangesDialogComponent(this.dialog, this.translateService, {
        pendingChangesDescription: this.translateService.instant('general.api_fees_unsaved_changes'),
        canNotSave: !this.isValid,
        buttonOk: !this.deliveryType.id ? this.translateService.instant('general.api_fees_create') :
          this.translateService.instant('general.api_fees_save')
      });
      pendingChanges.dialogRef.afterClosed().subscribe((res) => {
        if (res === true) {
          this.saveDeliveryType();
        } else if (res === CONTINUE_WITHOUT_SAVING) {
          this.close();
        }
      });
    }
  }

  private close(): void {
    this.dialogRef.disableClose = false;
    this.dialogRef.close();
  }

  public get periodPricesForPostList(): string[] {
    return Object.keys(this.periodPricesForPost) || [];
  }

  protected readonly NumericInputType = NumericInputType;
  protected readonly DELIVERY_TYPE_CLASSIFIER_MAX_LENGTH = DELIVERY_TYPE_CLASSIFIER_MAX_LENGTH;
  protected readonly ATTACHMENTS = ATTACHMENTS;
}
